import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, finalize, map, Observable, of, Subscription, switchMap } from "rxjs";
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  currentUser$!: Observable<any>;
  isLoading$!: Observable<boolean>;
  currentUserSubject!: BehaviorSubject<any>;
  isLoadingSubject!: BehaviorSubject<boolean>;
  tenantSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  get currentTenantValue(): any {
    return this.tenantSubject.value;
  }

  set currentTenantValue(tenant: any) {
    this.tenantSubject.next(tenant);
  }

  constructor(private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<any>({});
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getCurrentUser()?.subscribe();
    if (subscr)
      this.unsubscribe.push(subscr);
  }


  get isAuthenticated(): boolean {
    return this.currentUserValue?.id ? true : false
  }

  get isAdminOrOperator(): boolean {
    return this.currentUserValue?.id != undefined &&
      (this.currentUserValue.roles?.indexOf('SuperAdmin') > -1 || this.currentUserValue.roles?.indexOf('Admin') > -1 || this.currentUserValue.roles?.indexOf('SuperUser') > -1);
  }

  get isSuperAdmin(): boolean | undefined {
    return this.currentUserValue?.id != undefined &&
      this.currentUserValue.roles?.indexOf('SuperAdmin') > -1;
  }

  get isAdmin(): boolean | undefined {
    return this.currentUserValue?.id != undefined &&
      (this.currentUserValue.roles?.indexOf('SuperAdmin') > -1 || this.currentUserValue.roles?.indexOf('Admin') > -1);
  }

  get isSuperUser(): boolean | undefined {
    return this.currentUserValue?.id != undefined &&
      this.currentUserValue.roles?.indexOf('SuperUser') > -1;
  }

  get isTenantAdminOrOperator(): boolean | undefined {
    // Check if user is global Admin (which grants tenant-level access) or has tenant-specific roles
    if (this.currentUserValue?.id != undefined && this.currentUserValue.roles?.indexOf('Admin') > -1) {
      return true;
    }
    
    let is = this.currentUserValue?.id != undefined &&
      (this.currentTenantValue && (
        this.currentTenantValue.roles?.indexOf('TenantAdmin') > -1 ||
        this.currentTenantValue.roles?.indexOf('TeamMember') > -1
      ));

    return is;
  }

  get isTenantAdmin(): boolean | undefined {
    // Check if user is global Admin (which grants tenant-level access) or has TenantAdmin role
    if (this.currentUserValue?.id != undefined && this.currentUserValue.roles?.indexOf('Admin') > -1) {
      return true;
    }
    
    return this.currentUserValue?.id != undefined &&
      this.currentTenantValue && this.currentTenantValue.roles?.indexOf('TenantAdmin') > -1;
  }

  isTenantAdminById(tenantId: number): boolean | undefined {
    // Check if user is global Admin (which grants tenant-level access)
    if (this.currentUserValue?.id != undefined && this.currentUserValue.roles?.indexOf('Admin') > -1) {
      return true;
    }
    
    let tenant = null;
    try {
      const storedTenant = localStorage.getItem("tenant");
      if (storedTenant) {
        tenant = JSON.parse(storedTenant);
      }
    } catch (e) {
      console.warn('Error parsing tenant in isTenantAdminById', e);
    }
    
    return this.currentUserValue?.id != undefined &&
      tenant && tenant.roles?.indexOf('TenantAdmin') > -1;
  }

  get isTenantMember(): boolean | undefined {
    let is = this.currentUserValue?.id != undefined &&
      this.currentTenantValue && this.currentTenantValue.roles?.indexOf('TeamMember') > -1;

    return is;
  }

  isInRole(role: string): boolean {
    return this.currentUserValue?.roles?.indexOf(role) > -1;
  }

  changeTenant(tenant: any) {
    if (tenant) {
      localStorage.setItem("tenantId", tenant.id);
      localStorage.setItem("tenant", JSON.stringify(tenant));
      this.tenantSubject.next(tenant);
    }
  }

  userExists(username: any): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiUrl}/account/exists/`, { params: { username } });
  }

  login(value: any) {
    return this.http.post(`${environment.apiUrl}/Account/login`, value)
      .pipe(map((data: any) => {
        window.localStorage.setItem('token', data.token.accessToken)
        window.localStorage.setItem('refresh-token', data.token.refreshToken)
        window.localStorage.setItem('currentUser', JSON.stringify(data.user))
        this.currentUserSubject.next(data.user);
        return data;
      }))
  }

  otpLogin(value: any) {
    return this.http.post(`${environment.apiUrl}/account/otplogin`, value)
      .pipe(map((data: any) => {
        window.localStorage.setItem('token', data.token.accessToken)
        window.localStorage.setItem('refresh-token', data.token.refreshToken)
        window.localStorage.setItem('currentUser', JSON.stringify(data.user))
        this.currentUserSubject.next(data.user);
        return data;
      }))
  }

  refreshToken() {
    const token = localStorage.getItem("refresh-token");
    if (!token) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.http.post(`${environment.apiUrl}/account/refreshToken`, { refreshToken: token }).pipe(
      map((res: any) => {
        window.localStorage.setItem('token', res.token.accessToken)
        window.localStorage.setItem('refresh-token', res.token.refreshToken)
        window.localStorage.setItem('currentUser', JSON.stringify(res.user))
        this.currentUserSubject.next(res.user);
        return res;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  signUp(value: any) {
    return this.http.post(`${environment.apiUrl}/account/register`, value)
      .pipe(map((data: any) => {
        window.localStorage.setItem('detail', data)
      }), switchMap(() => this.login(value)))
  }

  signUpWithoutLogin(value: any) {
    return this.http.post(`${environment.apiUrl}/account/register`, value)
      .pipe(map((data: any) => {
        window.localStorage.setItem('detail', data)
      }));
  }

  sendOtp(value: any) {
    return this.http.post<string>(`${environment.apiUrl}/account/sendotp`, value);
  }

  sendOtpForRegister(value: any) {
    return this.http.post<string>(`${environment.apiUrl}/account/sendOtpForRegister`, value);
  }


  resendCode(username: any) {
    return this.http.post<string>(`${environment.apiUrl}/account/ResendCode`, username);
  }

  confirmCode(code: number, username: string): Observable<any> {
    return this.http
      .post<any>(
        `${environment.apiUrl}/account/ConfirmCode`,
        { username, code },
      )
      .pipe(
        map((x) => {
          window.localStorage.setItem('confirm-code', code.toString())
          return username
        }),
      );
  }

  setPassword(password: string, username: any, code: any): Observable<any> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/account/setpassword`,
        { email: username, password, ConfirmPassword: password, token: code },
      )
      .pipe(
        map(() => username),
      );
  }

  changePassword(changePassword: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/account/changepassword`, changePassword);
  }


  passwordRecovery(value: any) {
    return this.http.post(`${environment.apiUrl}/account/forgetpassword`, value)
      .pipe(map((data: any) => {
        window.localStorage.setItem('email', data)
      }))
  }

  setNewPass(value: any) {
    return this.http.post(`${environment.apiUrl}/account/resetpassword`, value);
  }

  getCurrentUser(): Observable<any> | null {

    if (isPlatformBrowser(this.platformId)) {
      const auth = this.getAuthFromLocalStorage();
      if (!auth) {
        return of(undefined);
      }

      this.currentUserSubject.next(auth);
      return of(auth);
    } else {
      return null;
    }
  }

  logout() {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenants');
    localStorage.removeItem('operatorInfo');
    localStorage.removeItem('detail');
    localStorage.removeItem('customerInfo');
    localStorage.removeItem('customerId');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('refresh-token');
    localStorage.removeItem('token');
    localStorage.removeItem('tableState');
    localStorage.removeItem('forms');
    localStorage.removeItem('activities-tableState');
    localStorage.removeItem('tabSelection');
    this.router.navigate(['/'], {
      queryParams: {},
    }).then(() => {
      this.currentUserSubject.next({});
    });
  }

  private getAuthFromLocalStorage() {
    try {
      const authData = JSON.parse(
        localStorage.getItem("currentUser") as string
      );
      return authData;
    } catch (error) {

      return undefined;
    }
  }

  addPushSubscriber(token: string) {
    return this.http.post(`${environment.apiUrl}/account/addPushSubscriber`, { token });
  }

  getPushSubscriber() {
    return this.http.get(`${environment.apiUrl}/account/getPushSubscriber`);
  }

  getLoginCount(): Observable<number> {
    return this.http.get<number>(`${environment.apiUrl}/account/getLoginCount`);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
